import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Search, UserCog, Ban, CheckCircle, Trash2, Edit, Filter, Shield, Plus, X, RefreshCw, Wrench, Users } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getMemberCategory, getMemberClassification, isPlaceholderMembershipNumber, getDigitalMembershipStatus } from "../lib/memberClassification";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  { value: "member", label: "Member" },
  { value: "non-member", label: "Non-Member" },
  { value: "team_captain", label: "Team Captain" },
  { value: "vice_captain", label: "Vice Captain" },
  { value: "team_admin", label: "Team Admin" },
  { value: "treasurer", label: "Treasurer" },
  { value: "secretary", label: "Secretary" },
  { value: "jsm_representative", label: "JSM Representative" },
  { value: "committee_member", label: "Committee Member" },
  { value: "event_manager", label: "Event Manager" },
  { value: "merchant", label: "Merchant" },
  { value: "captain", label: "Section Captain (Admin)" },
  { value: "convenor", label: "Convenor (Admin)" },
];

const TEAM_ROLES = [
  { value: "captain", label: "Team Captain" },
  { value: "vice_captain", label: "Vice Captain" },
  { value: "manager", label: "Team Manager" },
  { value: "admin", label: "Team Admin" },
  { value: "admin_manager", label: "Admin Manager" },
  { value: "social_secretary", label: "Social Secretary" },
  { value: "scribe", label: "Scribe" },
  { value: "social_planner", label: "Social Planner" },
];

const MEMBER_TYPES = [
  { value: "section", label: "Section Member" },
  { value: "club", label: "Club Member (SCC)" },
  { value: "digital_member", label: "Digital Member" },
  { value: "academy_parent", label: "Academy Parent Only" },
  { value: "academy_coach", label: "Academy Coach Only" },
  { value: "non-member", label: "Non-Member" },
  { value: "non-section", label: "Non-Section" },
];

// Category = the primary label from getMemberClassification(), exposed as a
// stable machine-readable key for filtering (the label string itself could
// change without breaking saved filters).
const CATEGORY_OPTIONS = [
  { value: "all", label: "All Categories" },
  { value: "section", label: "🟢 Section Member" },
  { value: "verified_scc", label: "🔵 Verified SCC Member" },
  { value: "pending", label: "🟡 Pending Verification" },
  { value: "digital_member", label: "🟦 Digital Member" },
  { value: "academy_only", label: "⚫ Academy only" },
  { value: "non_member", label: "🔴 Non-Member" },
];

export default function MemberManagement({ user }) {
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  
  // Selection state for bulk operations
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editingMemberChildren, setEditingMemberChildren] = useState([]);
  const [loadingEditChildren, setLoadingEditChildren] = useState(false);
  // Membership 10% refund/credit tool state (Phase ii)
  const [adjRegs, setAdjRegs] = useState(null);
  const [adjLoading, setAdjLoading] = useState(false);
  const [adjInputs, setAdjInputs] = useState({});
  const [editForm, setEditForm] = useState({
    role: "",
    member_type: "",
    teams: [],
    membership_number: "",
    is_academy_parent: false,
    is_academy_coach: false,
    is_academy_player: false,
  });
  
  // Link Children dialog state
  const [linkChildrenDialogOpen, setLinkChildrenDialogOpen] = useState(false);
  const [linkingParent, setLinkingParent] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [linkedChildren, setLinkedChildren] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [selectedChildrenToLink, setSelectedChildrenToLink] = useState([]);
  const [playerSearchQuery, setPlayerSearchQuery] = useState("");
  
  // Create Child dialog state
  const [createChildOpen, setCreateChildOpen] = useState(false);
  const [creatingChild, setCreatingChild] = useState(false);
  const [newChild, setNewChild] = useState({ given_name: "", last_name: "", birthdate: "", gender: "male", medical_conditions: "" });
  
  // Suspend dialog state
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendingMember, setSuspendingMember] = useState(null);
  const [suspendReason, setSuspendReason] = useState("Non-payment of fees");
  
  // Team roles dialog state
  const [teamRolesDialogOpen, setTeamRolesDialogOpen] = useState(false);
  const [teamRolesMember, setTeamRolesMember] = useState(null);
  const [newTeamRole, setNewTeamRole] = useState({ team_id: "", role: "" });

  useEffect(() => {
    fetchMembers();
    fetchTeams();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      // Show authorised members AND anyone with a membership status (digital /
      // SCC verified / claimed) so digital members are never hidden from the
      // convenor even if their account isn't flagged is_authorized.
      setMembers(response.data.filter(u =>
        u.is_authorized || u.is_digital_member || u.scc_member_verified || u.is_scc_member
      ));
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const [fixingClassifications, setFixingClassifications] = useState(false);
  
  const handleFixClassifications = async () => {
    setFixingClassifications(true);
    try {
      const response = await axios.post(`${API}/admin/fix-member-classifications`, {}, {
        withCredentials: true
      });
      if (response.data.fixed_count > 0) {
        toast.success(`Fixed ${response.data.fixed_count} member classification(s)`);
      } else {
        toast.success("All member classifications are correct!");
      }
      fetchMembers();
    } catch (error) {
      console.error("Error fixing classifications:", error);
      toast.error("Failed to fix member classifications");
    } finally {
      setFixingClassifications(false);
    }
  };

  // Link Children functions
  const openLinkChildrenDialog = async (parent) => {
    setLinkingParent(parent);
    setSelectedChildrenToLink([]);
    setPlayerSearchQuery("");
    setLinkChildrenDialogOpen(true);
    setLoadingPlayers(true);
    
    try {
      // Fetch all academy players using admin endpoint
      const response = await axios.get(`${API}/academy/admin/all-players`, { withCredentials: true });
      const allPlayers = response.data || [];
      
      // Separate linked and unlinked players
      const parentId = parent.id;
      const currentlyLinked = allPlayers.filter(p => p.parent_user_id === parentId);
      const unlinked = allPlayers.filter(p => !p.parent_user_id || p.parent_user_id !== parentId);
      
      setLinkedChildren(currentlyLinked);
      setAvailablePlayers(unlinked);
    } catch (error) {
      console.error("Error fetching players:", error);
      toast.error("Failed to load academy players");
    } finally {
      setLoadingPlayers(false);
    }
  };

  // Filter available players by search query
  const filteredAvailablePlayers = availablePlayers.filter(player => {
    if (!playerSearchQuery.trim()) return true;
    const query = playerSearchQuery.toLowerCase();
    const name = (player.name || player.child_name || "").toLowerCase();
    const membershipNum = (player.membership_number || "").toLowerCase();
    return name.includes(query) || membershipNum.includes(query);
  });

  const handleLinkChildren = async () => {
    if (selectedChildrenToLink.length === 0) {
      toast.error("Please select at least one child to link");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/link-children`,
        {
          parent_id: linkingParent.id,
          player_ids: selectedChildrenToLink
        },
        { withCredentials: true }
      );
      
      toast.success(`Linked ${selectedChildrenToLink.length} child(ren) to ${linkingParent.name}`);
      
      // Refresh the list
      openLinkChildrenDialog(linkingParent);
      setSelectedChildrenToLink([]);
    } catch (error) {
      console.error("Error linking children:", error);
      toast.error(error.response?.data?.detail || "Failed to link children");
    }
  };

  const handleUnlinkChild = async (playerId, playerName) => {
    if (!window.confirm(`Unlink ${playerName} from ${linkingParent.name}?`)) return;
    
    try {
      await axios.post(
        `${API}/admin/unlink-child`,
        {
          parent_id: linkingParent.id,
          player_id: playerId
        },
        { withCredentials: true }
      );
      
      toast.success(`Unlinked ${playerName}`);
      openLinkChildrenDialog(linkingParent);
    } catch (error) {
      console.error("Error unlinking child:", error);
      toast.error("Failed to unlink child");
    }
  };

  const handleCreateChild = async () => {
    if (!linkingParent || !newChild.given_name || !newChild.last_name || !newChild.birthdate) {
      toast.error("Please fill in child's name and date of birth");
      return;
    }
    setCreatingChild(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/create-child-for-parent`,
        { parent_id: linkingParent.id, ...newChild },
        { withCredentials: true }
      );
      toast.success(`${res.data.message} (${res.data.membership_number})`, { duration: 6000 });
      setCreateChildOpen(false);
      setNewChild({ given_name: "", last_name: "", birthdate: "", gender: "male", medical_conditions: "" });
      openLinkChildrenDialog(linkingParent);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create child");
    } finally { setCreatingChild(false); }
  };


  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const openEditDialog = async (member) => {
    setEditingMember(member);
    setAdjRegs(null);
    setAdjInputs({});
    setEditingMemberChildren([]);
    const roles = member.roles || [];
    // Build teams array from legacy fields if not present
    let memberTeams = member.teams || [];
    if (memberTeams.length === 0) {
      if (member.primary_team || member.team_id) {
        memberTeams.push(member.primary_team || member.team_id);
      }
      if (member.second_team && !memberTeams.includes(member.second_team)) {
        memberTeams.push(member.second_team);
      }
    }
    setEditForm({
      role: member.role || "member",
      member_type: member.member_type || "non-section",
      teams: memberTeams,
      membership_number: member.membership_number || "",
      is_academy_parent: roles.includes("academy_parent"),
      is_academy_coach: roles.includes("academy_coach"),
      is_academy_player: roles.includes("academy_player"),
      is_digital_member: !!member.is_digital_member,
      digital_membership_number: member.digital_membership_number || "",
      digital_membership_paid_date: member.digital_membership_paid_date ? String(member.digital_membership_paid_date).slice(0, 10) : "",
      digital_membership_expires_at: member.digital_membership_expires_at ? String(member.digital_membership_expires_at).slice(0, 10) : "",
      scc_member_verified: !!member.scc_member_verified,
    });
    setEditDialogOpen(true);
    
    // Fetch linked children if academy parent
    if (roles.includes("academy_parent")) {
      fetchEditingMemberChildren(member.id);
    }
  };

  const fetchEditingMemberChildren = async (parentId) => {
    setLoadingEditChildren(true);
    try {
      const response = await axios.get(`${API}/academy/admin/all-players`, { withCredentials: true });
      const allPlayers = response.data || [];
      const children = allPlayers.filter(p => p.parent_user_id === parentId);
      setEditingMemberChildren(children);
    } catch (error) {
      console.error("Error fetching children:", error);
    } finally {
      setLoadingEditChildren(false);
    }
  };

  const handleUpdateMember = async () => {
    // Build the roles array based on checkboxes
    const additionalRoles = [];
    if (editForm.is_academy_parent) additionalRoles.push("academy_parent");
    if (editForm.is_academy_coach) additionalRoles.push("academy_coach");
    if (editForm.is_academy_player) additionalRoles.push("academy_player");
    
    const updateData = {
      role: editForm.role,
      member_type: editForm.member_type,
      teams: editForm.teams,  // Send flexible teams array
      membership_number: editForm.membership_number,
      roles: additionalRoles,
      is_digital_member: editForm.is_digital_member,
      digital_membership_number: editForm.digital_membership_number,
      digital_membership_paid_date: editForm.digital_membership_paid_date,
      digital_membership_expires_at: editForm.digital_membership_expires_at,
      scc_member_verified: editForm.scc_member_verified,
    };
    
    try {
      await axios.put(
        `${API}/admin/members/${editingMember.id}`,
        updateData,
        { withCredentials: true }
      );
      toast.success(`${editingMember.name} updated successfully`);
      setEditDialogOpen(false);
      fetchMembers();
    } catch (error) {
      console.error("Error updating member:", error);
      toast.error(error.response?.data?.detail || "Failed to update member");
    }
  };

  const loadAdjustments = async (parentId) => {
    setAdjLoading(true);
    try {
      const res = await axios.get(`${API}/academy/admin/parent/${parentId}/membership-adjustments-available`, { withCredentials: true });
      const regs = res.data?.registrations || [];
      setAdjRegs(regs);
      const seed = {};
      regs.forEach((r) => {
        seed[r.registration_id] = { method: "credit", amount: r.suggested_adjustment, reference: "", note: "" };
      });
      setAdjInputs(seed);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load registrations");
    } finally {
      setAdjLoading(false);
    }
  };

  const issueAdjustment = async (regId) => {
    const inp = adjInputs[regId] || {};
    if (!inp.amount || Number(inp.amount) <= 0) { toast.error("Enter an amount greater than 0"); return; }
    if (inp.method === "refund" && !(inp.reference || "").trim()) { toast.error("A refund reference (e.g. Wise ID) is required"); return; }
    try {
      const res = await axios.post(
        `${API}/academy/admin/registration/${regId}/membership-adjustment`,
        { method: inp.method, amount: Number(inp.amount), reference: inp.reference || null, note: inp.note || null },
        { withCredentials: true }
      );
      toast.success(`${inp.method === "credit" ? "Account credit" : "Refund"} of $${res.data.amount.toFixed(2)} recorded`);
      loadAdjustments(editingMember.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record adjustment");
    }
  };
  
  const addTeamToMember = (teamId) => {
    if (teamId && !editForm.teams.includes(teamId)) {
      setEditForm({ ...editForm, teams: [...editForm.teams, teamId] });
    }
  };
  
  const removeTeamFromMember = (teamId) => {
    setEditForm({ ...editForm, teams: editForm.teams.filter(t => t !== teamId) });
  };

  const openTeamRolesDialog = (member) => {
    setTeamRolesMember(member);
    setNewTeamRole({ team_id: "", role: "" });
    setTeamRolesDialogOpen(true);
  };

  const addTeamRole = async () => {
    if (!newTeamRole.team_id || !newTeamRole.role) {
      toast.error("Please select both team and role");
      return;
    }
    try {
      await axios.post(
        `${API}/users/${teamRolesMember.id}/team-roles`,
        { user_id: teamRolesMember.id, team_id: newTeamRole.team_id, role: newTeamRole.role },
        { withCredentials: true }
      );
      toast.success("Team role added");
      setNewTeamRole({ team_id: "", role: "" });
      fetchMembers();
      // Update local state
      const updatedMember = members.find(m => m.id === teamRolesMember.id);
      if (updatedMember) {
        setTeamRolesMember({ ...updatedMember, team_roles: [...(updatedMember.team_roles || []), newTeamRole] });
      }
    } catch (error) {
      console.error("Error adding team role:", error);
      toast.error(error.response?.data?.detail || "Failed to add team role");
    }
  };

  const removeTeamRole = async (teamId, role) => {
    try {
      await axios.delete(
        `${API}/users/${teamRolesMember.id}/team-roles/${teamId}/${role}`,
        { withCredentials: true }
      );
      toast.success("Team role removed");
      fetchMembers();
      // Update local state
      setTeamRolesMember(prev => ({
        ...prev,
        team_roles: (prev.team_roles || []).filter(r => !(r.team_id === teamId && r.role === role))
      }));
    } catch (error) {
      console.error("Error removing team role:", error);
      toast.error("Failed to remove team role");
    }
  };

  const openSuspendDialog = (member) => {
    setSuspendingMember(member);
    setSuspendReason("Non-payment of fees");
    setSuspendDialogOpen(true);
  };

  const handleSuspend = async () => {
    try {
      await axios.post(
        `${API}/admin/members/${suspendingMember.id}/suspend?reason=${encodeURIComponent(suspendReason)}`,
        {},
        { withCredentials: true }
      );
      toast.success(`${suspendingMember.name} has been suspended`);
      setSuspendDialogOpen(false);
      fetchMembers();
    } catch (error) {
      console.error("Error suspending member:", error);
      toast.error(error.response?.data?.detail || "Failed to suspend member");
    }
  };

  const handleReactivate = async (member) => {
    if (!window.confirm(`Reactivate ${member.name}'s account?`)) return;
    
    try {
      await axios.post(
        `${API}/admin/members/${member.id}/reactivate`,
        {},
        { withCredentials: true }
      );
      toast.success(`${member.name} has been reactivated`);
      fetchMembers();
    } catch (error) {
      console.error("Error reactivating member:", error);
      toast.error(error.response?.data?.detail || "Failed to reactivate member");
    }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Permanently delete ${member.name}? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/admin/members/${member.id}`, { withCredentials: true });
      toast.success(`${member.name} has been deleted`);
      fetchMembers();
    } catch (error) {
      console.error("Error deleting member:", error);
      toast.error(error.response?.data?.detail || "Failed to delete member");
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    const selectableIds = filteredMembers
      .filter(m => m.id !== user?.id) // Can't select yourself
      .map(m => m.id);
    
    if (selectedIds.length === selectableIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableIds);
    }
  };

  const toggleSelectMember = (memberId) => {
    if (selectedIds.includes(memberId)) {
      setSelectedIds(selectedIds.filter(id => id !== memberId));
    } else {
      setSelectedIds([...selectedIds, memberId]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error("No members selected");
      return;
    }

    if (!window.confirm(`Permanently delete ${selectedIds.length} selected member(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/members/bulk-delete`,
        { user_ids: selectedIds },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setSelectedIds([]);
      fetchMembers();
    } catch (error) {
      console.error("Error bulk deleting members:", error);
      toast.error(error.response?.data?.detail || "Failed to delete members");
    }
  };

  // Filter members
  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.membership_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === "all" || member.role === filterRole;
    
    const matchesStatus = 
      filterStatus === "all" ||
      (filterStatus === "active" && !member.is_suspended) ||
      (filterStatus === "suspended" && member.is_suspended);
    
    const matchesCategory = filterCategory === "all" || getMemberCategory(member) === filterCategory;
    
    return matchesSearch && matchesRole && matchesStatus && matchesCategory;
  });

  // Per-category counts across the full (non-filtered) member list — used both
  // for the stat strip and for the dropdown trigger hint.
  const categoryCounts = members.reduce((acc, m) => {
    const k = getMemberCategory(m);
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || "No team";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-800/50 flex items-center justify-center">
        <p>Loading members...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-800/50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin?tab=tools">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white">Member Management</h1>
          </div>
          <Button 
            variant="outline" 
            onClick={handleFixClassifications}
            disabled={fixingClassifications}
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
            title="Fix incorrect member classifications (academy parents, merchants)"
          >
            {fixingClassifications ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Wrench className="w-4 h-4 mr-2" />
            )}
            Fix Classifications
          </Button>
        </div>

        {/* Search and Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, or member #"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="member-search-input"
                />
              </div>
            </div>
            
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {ROLES.map(role => (
                  <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[220px]" data-testid="filter-category-trigger">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    data-testid={`filter-category-${opt.value}`}
                  >
                    <span className="flex items-center justify-between gap-3 w-full">
                      <span>{opt.label}</span>
                      {opt.value !== "all" && categoryCounts[opt.value] !== undefined && (
                        <span className="text-xs text-slate-400 font-mono">{categoryCounts[opt.value]}</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Card className="p-4 bg-blue-500/10">
            <p className="text-sm text-blue-400">Total</p>
            <p className="text-2xl font-bold text-blue-400">{members.length}</p>
          </Card>
          <Card className="p-4 bg-emerald-500/10">
            <p className="text-sm text-emerald-400">Section</p>
            <p className="text-2xl font-bold text-emerald-400">{categoryCounts["section"] || 0}</p>
          </Card>
          <Card
            className="p-4 bg-amber-500/10 cursor-pointer hover:bg-amber-500/20 transition"
            onClick={() => setFilterCategory("pending")}
            data-testid="stat-card-pending"
            title="Click to filter the list to pending verifications"
          >
            <p className="text-sm text-amber-400">🟡 Pending verify</p>
            <p className="text-2xl font-bold text-amber-400">{categoryCounts["pending"] || 0}</p>
          </Card>
          <Card className="p-4 bg-slate-700/30">
            <p className="text-sm text-slate-300">Academy only</p>
            <p className="text-2xl font-bold text-slate-200">{categoryCounts["academy_only"] || 0}</p>
          </Card>
          <Card className="p-4 bg-red-500/10">
            <p className="text-sm text-red-400">Suspended</p>
            <p className="text-2xl font-bold text-red-400">
              {members.filter(m => m.is_suspended).length}
            </p>
          </Card>
        </div>

        {/* Members List */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              Members ({filteredMembers.length})
            </h2>
            
            {/* Bulk Actions */}
            <div className="flex items-center gap-3">
              {selectedIds.length > 0 && (
                <span className="text-sm text-slate-400">
                  {selectedIds.length} selected
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAll}
                className="text-slate-400"
              >
                {selectedIds.length === filteredMembers.filter(m => m.id !== user?.id).length 
                  ? "Deselect All" 
                  : "Select All"}
              </Button>
              {selectedIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected ({selectedIds.length})
                </Button>
              )}
            </div>
          </div>
          
          <div className="space-y-3">
            {filteredMembers.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No members found</p>
            ) : (
              filteredMembers.map((member, _idx) => (
                <div
                  key={`${member.id}-${_idx}`}
                  className={`p-4 border rounded-lg ${
                    selectedIds.includes(member.id)
                      ? "border-blue-500/30 bg-blue-500/10"
                      : member.is_suspended 
                        ? "border-red-500/30 bg-red-500/10" 
                        : "border-white/5 bg-slate-900/50"
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    {/* Checkbox */}
                    <div className="flex items-center">
                      {member.id !== user?.id && (
                        <Checkbox
                          checked={selectedIds.includes(member.id)}
                          onCheckedChange={() => toggleSelectMember(member.id)}
                          className="mr-3"
                        />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-white">{member.name}</p>
                        {member.is_suspended && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                            Suspended
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-400">{member.email}</p>
                      <div className="flex flex-wrap gap-2 mt-1 items-center">
                        {member.membership_number && !isPlaceholderMembershipNumber(member.membership_number) && (
                          <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded font-mono" title="Membership number (admin/convenor only)" data-testid={`membership-number-${member.id}`}>
                            #{member.membership_number}
                          </span>
                        )}
                        {isPlaceholderMembershipNumber(member.membership_number) && (
                          <span className="text-xs bg-slate-700/60 text-slate-300 px-2 py-0.5 rounded font-mono" title="System-generated at approval — NOT a verified SCC membership number" data-testid={`placeholder-number-${member.id}`}>
                            #{member.membership_number} · auto-generated, not verified
                          </span>
                        )}
                        {member.scc_member_verified && (
                          <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded" data-testid={`verified-badge-${member.id}`}>
                            ✓ SCC Verified
                            {member.scc_member_verified_at ? ` · ${new Date(member.scc_member_verified_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
                            {member.member_category && ` (${member.member_category}${member.member_family_relationship ? ` · ${member.member_family_relationship}` : ""})`}
                          </span>
                        )}
                        {!member.membership_number && (
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                            No membership # on file
                          </span>
                        )}
                        {(() => {
                          const dm = getDigitalMembershipStatus(member);
                          if (!dm.isDM) return null;
                          return (
                            <span className={`text-xs px-2 py-0.5 rounded ${dm.badgeColor}`} data-testid={`dm-status-${member.id}`}>
                              💳 {dm.number ? `${dm.number} · ` : ""}{dm.badgeLabel}{dm.start ? ` · from ${dm.start}` : ""}
                            </span>
                          );
                        })()}
                      </div>
                      {member.is_suspended && member.suspension_reason && (
                        <p className="text-xs text-red-400 mt-1">
                          Reason: {member.suspension_reason}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Member Classification - Single comprehensive badge + secondary DM pill */}
                      {(() => {
                        const classification = getMemberClassification(member);
                        return (
                          <>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${classification.colorClass}`} data-testid={`member-badge-${classification.primaryKey}`}>
                              {classification.label}
                            </span>
                            {(classification.secondaryTags || []).map((tag) => (
                              <span
                                key={tag.label}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${tag.colorClass}`}
                                title="Active Digital Membership"
                                data-testid={`member-secondary-tag-${tag.label.toLowerCase()}`}
                              >
                                {tag.label}
                              </span>
                            ))}
                          </>
                        );
                      })()}
                      
                      {/* Show all teams for section members */}
                      {member.member_type === "section" && member.teams && member.teams.length > 1 && (
                        <div className="flex flex-wrap gap-1">
                          {member.teams.slice(1).map((teamId, idx) => (
                            <span 
                              key={teamId} 
                              className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs"
                              title={`Team ${idx + 2}`}
                            >
                              +{getTeamName(teamId)}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Fallback for legacy second_team field */}
                      {member.member_type === "section" && !member.teams?.length && member.second_team && (
                        <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs" title="2nd Team">
                          2nd: {getTeamName(member.second_team)}
                        </span>
                      )}
                      
                      {/* Show team roles */}
                      {member.team_roles && member.team_roles.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {member.team_roles.map((tr, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                              {tr.role} @ {tr.team_name || getTeamName(tr.team_id)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(member)}
                        title="Edit member"
                        data-testid={`edit-member-btn-${member.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      
                      {/* Link Children button - show for Academy Parents */}
                      {member.roles?.includes("academy_parent") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openLinkChildrenDialog(member)}
                          title="Link/manage children"
                          className="text-blue-400 hover:text-blue-400 hover:bg-blue-500/10"
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTeamRolesDialog(member)}
                        title="Manage team roles"
                        className="text-emerald-400 hover:text-emerald-400"
                      >
                        <Shield className="w-4 h-4" />
                      </Button>
                      
                      {member.id !== user?.id && (
                        <>
                          {member.is_suspended ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReactivate(member)}
                              className="text-emerald-400 hover:text-emerald-400 hover:bg-emerald-500/10"
                              title="Reactivate member"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openSuspendDialog(member)}
                              className="text-orange-400 hover:text-orange-400 hover:bg-orange-500/10"
                              title="Suspend member"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(member)}
                            className="text-red-400 hover:text-red-400 hover:bg-red-500/10"
                            title="Delete member"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Member: {editingMember?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">Role</label>
              <Select
                value={editForm.role}
                onValueChange={(value) => setEditForm({ ...editForm, role: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Member Type</label>
              <Select
                value={editForm.member_type}
                onValueChange={(value) => setEditForm({ ...editForm, member_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {MEMBER_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Additional Roles */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium mb-3">Additional Roles</label>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="is_academy_parent"
                    checked={editForm.is_academy_parent}
                    onCheckedChange={(checked) => setEditForm({ ...editForm, is_academy_parent: checked })}
                  />
                  <label htmlFor="is_academy_parent" className="text-sm cursor-pointer">
                    Academy Parent
                    <span className="text-slate-400 ml-2 text-xs">(has children enrolled in Academy)</span>
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="is_academy_coach"
                    checked={editForm.is_academy_coach}
                    onCheckedChange={(checked) => setEditForm({ ...editForm, is_academy_coach: checked })}
                  />
                  <label htmlFor="is_academy_coach" className="text-sm cursor-pointer">
                    Academy Coach
                    <span className="text-slate-400 ml-2 text-xs">(coaches Academy sessions)</span>
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="is_academy_player"
                    checked={editForm.is_academy_player}
                    onCheckedChange={(checked) => setEditForm({ ...editForm, is_academy_player: checked })}
                  />
                  <label htmlFor="is_academy_player" className="text-sm cursor-pointer">
                    Academy Player
                    <span className="text-slate-400 ml-2 text-xs">(child enrolled in Academy)</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Membership Number</label>
              <Input
                value={editForm.membership_number || ""}
                onChange={(e) => setEditForm({ ...editForm, membership_number: e.target.value })}
                placeholder="e.g., L1234 or 24C0001"
              />
              {editingMember?.scc_member_verified && (
                <div className="mt-2 flex items-center justify-between p-2 rounded border border-emerald-500/30 bg-emerald-500/5">
                  <div className="text-xs text-emerald-300">
                    <p className="font-medium">✓ SCC Member Verified</p>
                    <p className="text-emerald-200/70">
                      {editingMember.member_category}
                      {editingMember.member_family_relationship && ` · ${editingMember.member_family_relationship} of ${editingMember.member_primary_member_name || "primary member"}`}
                      {editingMember.scc_member_verified_at && ` · on ${editingMember.scc_member_verified_at.slice(0, 10)}`}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="text-red-300 border-red-500/40 hover:bg-red-500/10 h-7 text-xs"
                    data-testid="revoke-member-btn"
                    onClick={async () => {
                      if (!window.confirm(`Revoke SCC membership verification for ${editingMember.name || editingMember.email}?\n\nThey will keep their account but future registrations will require re-verification if they claim membership.`)) return;
                      try {
                        const res = await axios.post(`${API}/payments/admin/revoke-member-verification/${editingMember.id}`, {}, { withCredentials: true });
                        toast.success(res.data.message || "Membership verification revoked");
                        setEditDialogOpen(false);
                        fetchMembers();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to revoke membership");
                      }
                    }}>
                    Revoke
                  </Button>
                </div>
              )}

              {editingMember && !editingMember?.scc_member_verified && (
                <label className="mt-2 flex items-start gap-2 p-2 rounded border border-blue-500/30 bg-blue-500/5 cursor-pointer" data-testid="edit-verify-scc-wrap">
                  <input
                    type="checkbox"
                    checked={!!editForm.scc_member_verified}
                    onChange={(e) => setEditForm({ ...editForm, scc_member_verified: e.target.checked })}
                    data-testid="edit-verify-scc-toggle"
                    className="mt-0.5"
                  />
                  <span className="text-xs text-blue-200">
                    <span className="font-medium text-blue-300">Verify SCC Membership</span> — tick only after you've confirmed this member's number against the known SCC formats. This unlocks the club <strong>10% discount</strong> on their registrations. (Save to apply.)
                  </span>
                </label>
              )}

              {editingMember && (
                <div className="mt-2 p-3 rounded border border-teal-500/30 bg-teal-500/5" data-testid="edit-dm-info">
                  <label className="flex items-center gap-2 text-xs font-medium text-teal-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editForm.is_digital_member}
                      onChange={(e) => setEditForm({ ...editForm, is_digital_member: e.target.checked })}
                      data-testid="edit-dm-toggle"
                    />
                    💳 Digital Membership
                  </label>
                  {editForm.is_digital_member && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                      <div>
                        <span className="text-[10px] text-slate-400 block mb-0.5">DM Number</span>
                        <input
                          type="text"
                          placeholder="DM-2026-0001"
                          value={editForm.digital_membership_number}
                          onChange={(e) => setEditForm({ ...editForm, digital_membership_number: e.target.value })}
                          className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white"
                          data-testid="edit-dm-number"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 block mb-0.5">Start (signup) date</span>
                        <input
                          type="date"
                          value={editForm.digital_membership_paid_date}
                          onChange={(e) => setEditForm({ ...editForm, digital_membership_paid_date: e.target.value })}
                          className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white"
                          data-testid="edit-dm-start"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 block mb-0.5">Expiry date</span>
                        <input
                          type="date"
                          value={editForm.digital_membership_expires_at}
                          onChange={(e) => setEditForm({ ...editForm, digital_membership_expires_at: e.target.value })}
                          className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white"
                          data-testid="edit-dm-expiry"
                        />
                      </div>
                    </div>
                  )}
                  <p className="text-[10px] text-slate-500 mt-1">Convenor can correct/backfill these directly. Leave expiry blank if unknown.</p>
                </div>
              )}

              {editingMember && (editingMember?.scc_member_verified || editingMember?.is_digital_member) && (
                <div className="mt-3 p-3 rounded border border-amber-500/30 bg-amber-500/5" data-testid="membership-adjustment-section">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-amber-300">10% Membership refund / credit</p>
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
                      data-testid="load-adjustments-btn"
                      onClick={() => loadAdjustments(editingMember.id)} disabled={adjLoading}>
                      {adjLoading ? "Loading…" : (adjRegs === null ? "Find eligible registrations" : "Refresh")}
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    For registrations charged full price before this member was verified. The suggested amount is the exact 10% the fee engine would have discounted. Refunds are recorded only (pay externally via Wise, then log the reference here); credits post to the parent's account.
                  </p>
                  {adjRegs !== null && adjRegs.length === 0 && (
                    <p className="text-xs text-slate-400 mt-2" data-testid="no-adjustments">No registrations found that would benefit from a 10% adjustment.</p>
                  )}
                  {adjRegs && adjRegs.map((r) => {
                    const inp = adjInputs[r.registration_id] || {};
                    return (
                      <div key={r.registration_id} className="mt-2 p-2 rounded bg-slate-800/60 border border-slate-700" data-testid={`adjustment-row-${r.registration_id}`}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-200 font-medium">{r.term_name || "Registration"}</span>
                          <span className="text-slate-400">Charged ${Number(r.charged_total).toFixed(2)} · suggested <span className="text-amber-300">${Number(r.suggested_adjustment).toFixed(2)}</span></span>
                        </div>
                        {r.already_adjusted && (
                          <p className="text-[10px] text-emerald-300 mt-1" data-testid={`adjustment-prior-${r.registration_id}`}>
                            ⚠ Already adjusted: {r.prior_adjustments.map((p) => `${p.method} $${Number(p.amount).toFixed(2)}${p.reference ? ` (${p.reference})` : ""}`).join(", ")}
                          </p>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 items-end">
                          <div>
                            <span className="text-[10px] text-slate-400 block mb-0.5">Method</span>
                            <select value={inp.method || "credit"}
                              onChange={(e) => setAdjInputs({ ...adjInputs, [r.registration_id]: { ...inp, method: e.target.value } })}
                              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white"
                              data-testid={`adjustment-method-${r.registration_id}`}>
                              <option value="credit">Credit</option>
                              <option value="refund">Refund</option>
                            </select>
                          </div>
                          <div>
                            <span className="text-[10px] text-slate-400 block mb-0.5">Amount ($)</span>
                            <input type="number" step="0.01" value={inp.amount ?? ""}
                              onChange={(e) => setAdjInputs({ ...adjInputs, [r.registration_id]: { ...inp, amount: e.target.value } })}
                              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white"
                              data-testid={`adjustment-amount-${r.registration_id}`} />
                          </div>
                          {inp.method === "refund" && (
                            <div>
                              <span className="text-[10px] text-slate-400 block mb-0.5">Wise reference *</span>
                              <input type="text" placeholder="e.g. WISE-8821" value={inp.reference || ""}
                                onChange={(e) => setAdjInputs({ ...adjInputs, [r.registration_id]: { ...inp, reference: e.target.value } })}
                                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white"
                                data-testid={`adjustment-reference-${r.registration_id}`} />
                            </div>
                          )}
                          <div>
                            <Button type="button" size="sm" className="h-7 text-xs w-full bg-amber-600 hover:bg-amber-500"
                              data-testid={`issue-adjustment-${r.registration_id}`}
                              onClick={() => issueAdjustment(r.registration_id)}>
                              {inp.method === "refund" ? "Record refund" : "Issue credit"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Only show team assignment for section members */}
            {editForm.member_type === "section" && (
              <div>
                <label className="block text-sm font-medium mb-2">Teams</label>
                
                {/* Currently assigned teams as tags */}
                <div className="flex flex-wrap gap-2 mb-3 min-h-[36px] p-2 border border-slate-700 rounded-md bg-slate-800/50">
                  {editForm.teams && editForm.teams.length > 0 ? (
                    editForm.teams.map((teamId, index) => {
                      const team = teams.find(t => t.id === teamId);
                      return (
                        <span 
                          key={teamId} 
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm ${
                            index === 0 
                              ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' 
                              : 'bg-slate-700 text-slate-300'
                          }`}
                        >
                          {index === 0 && <span className="text-xs font-medium">1st:</span>}
                          {team?.name || teamId}
                          <button
                            type="button"
                            onClick={() => removeTeamFromMember(teamId)}
                            className="ml-1 hover:text-red-400 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })
                  ) : (
                    <span className="text-slate-500 text-sm">No teams assigned</span>
                  )}
                </div>
                
                {/* Add team dropdown */}
                <Select
                  value=""
                  onValueChange={(value) => addTeamToMember(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="+ Add team..." />
                  </SelectTrigger>
                  <SelectContent>
                    {teams
                      .filter(team => !editForm.teams?.includes(team.id))
                      .map(team => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    {teams.filter(team => !editForm.teams?.includes(team.id)).length === 0 && (
                      <div className="px-2 py-1.5 text-sm text-slate-400">All teams assigned</div>
                    )}
                  </SelectContent>
                </Select>
                
                <p className="text-xs text-slate-400 mt-2">
                  First team is primary. Players go to standby for non-primary team games (if team priority enabled).
                </p>
              </div>
            )}
          </div>
          
          {/* Linked Children Section - Show if Academy Parent */}
          {editForm.is_academy_parent && (
            <div className="border-t border-slate-700 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium">
                  Linked Children ({editingMemberChildren.length})
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditDialogOpen(false);
                    openLinkChildrenDialog(editingMember);
                  }}
                  className="text-blue-400 border-blue-500/50 hover:bg-blue-500/10"
                >
                  <Users className="w-4 h-4 mr-1" />
                  Manage
                </Button>
              </div>
              
              {loadingEditChildren ? (
                <div className="text-center py-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-blue-400 mx-auto" />
                </div>
              ) : editingMemberChildren.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-2">No children linked yet. Click "Manage" to link.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {editingMemberChildren.map(child => {
                    const paymentColors = {
                      paid: 'bg-green-500/20 text-green-400 border-green-500/30',
                      complete: 'bg-green-500/20 text-green-400 border-green-500/30',
                      verified: 'bg-green-500/20 text-green-400 border-green-500/30',
                      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                      overdue: 'bg-red-500/20 text-red-400 border-red-500/30',
                      no_enrollment: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
                    };
                    const statusClass = paymentColors[child.payment_status] || paymentColors.pending;
                    
                    return (
                      <div key={child.id} className="p-3 bg-slate-800/50 border border-slate-700 rounded">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                            <span className="text-white font-medium">{child.name || child.child_name}</span>
                            {child.membership_number && (
                              <span className="text-slate-400 text-sm">#{child.membership_number}</span>
                            )}
                          </div>
                          <span className="text-xs text-slate-500">{child.age_group || child.team_name}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          {child.term_name && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                              {child.term_name}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded border ${statusClass}`}>
                            {(child.payment_status || 'unknown').replace('_', ' ').toUpperCase()}
                          </span>
                          {child.sessions_remaining !== undefined && child.sessions_remaining !== null && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">
                              {child.sessions_remaining} sessions left
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateMember} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend Member: {suspendingMember?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-slate-400 mb-4">
              Suspending this member will prevent them from logging in until reactivated.
            </p>
            
            <label className="block text-sm font-medium mb-2">Reason for Suspension</label>
            <Textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Enter reason for suspension..."
              rows={3}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSuspend} className="bg-red-600 hover:bg-red-700">
              Suspend Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Roles Dialog */}
      <Dialog open={teamRolesDialogOpen} onOpenChange={setTeamRolesDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Team Roles: {teamRolesMember?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              Assign multiple team roles (e.g., Captain of Team A, Manager of Team B)
            </p>
            
            {/* Current Team Roles */}
            <div>
              <label className="block text-sm font-medium mb-2">Current Team Roles</label>
              {(!teamRolesMember?.team_roles || teamRolesMember.team_roles.length === 0) ? (
                <p className="text-sm text-slate-400 italic">No team roles assigned</p>
              ) : (
                <div className="space-y-2">
                  {teamRolesMember.team_roles.map((tr, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-[#0a0f1a] rounded">
                      <span className="text-sm">
                        <span className="font-medium capitalize">{tr.role}</span>
                        {" of "}
                        <span className="text-blue-400">{tr.team_name || getTeamName(tr.team_id)}</span>
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeTeamRole(tr.team_id, tr.role)}
                        className="text-red-400 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Add New Team Role */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium mb-2">Add New Team Role</label>
              <div className="grid grid-cols-2 gap-2">
                <Select
                  value={newTeamRole.team_id}
                  onValueChange={(value) => setNewTeamRole({ ...newTeamRole, team_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select
                  value={newTeamRole.role}
                  onValueChange={(value) => setNewTeamRole({ ...newTeamRole, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {TEAM_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={addTeamRole}
                className="mt-2 w-full bg-green-600 hover:bg-green-700"
                disabled={!newTeamRole.team_id || !newTeamRole.role}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Team Role
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamRolesDialogOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Children Dialog */}
      <Dialog open={linkChildrenDialogOpen} onOpenChange={setLinkChildrenDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Link Children: {linkingParent?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            {/* Currently Linked Children */}
            <div>
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Currently Linked Children ({linkedChildren.length})
              </h3>
              
              {linkedChildren.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-400 italic mb-3">No children linked yet</p>
                  <Button onClick={() => { setCreateChildOpen(true); setNewChild({ given_name: "", last_name: linkingParent?.name?.split(" ").pop() || "", birthdate: "", gender: "male", medical_conditions: "" }); }}
                    className="bg-emerald-600 hover:bg-emerald-700" size="sm">
                    <Plus className="w-4 h-4 mr-2" />Create Child for This Parent
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {linkedChildren.map(child => (
                    <div key={child.id} className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div>
                        <span className="font-medium text-white">{child.name || child.child_name}</span>
                        <span className="ml-2 text-sm text-slate-400">
                          {child.membership_number && `#${child.membership_number}`}
                        </span>
                        <span className="ml-2 text-xs text-green-400">{child.age_group || child.team_id}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnlinkChild(child.id, child.name || child.child_name)}
                        className="text-red-400 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Available Players to Link */}
            <div>
              <h3 className="font-semibold text-white mb-3">
                Available Players to Link ({availablePlayers.length})
              </h3>
              
              {/* Search Box */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or membership #..."
                  value={playerSearchQuery}
                  onChange={(e) => setPlayerSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-600"
                />
              </div>
              
              {loadingPlayers ? (
                <div className="text-center py-4">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-400 mx-auto" />
                  <p className="text-sm text-slate-400 mt-2">Loading players...</p>
                </div>
              ) : filteredAvailablePlayers.length === 0 ? (
                <p className="text-sm text-slate-400 italic">
                  {playerSearchQuery ? `No players matching "${playerSearchQuery}"` : "No unlinked players available"}
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {filteredAvailablePlayers.map(player => (
                    <label
                      key={player.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedChildrenToLink.includes(player.id)
                          ? 'bg-blue-500/20 border border-blue-500/50'
                          : 'bg-slate-800/50 border border-slate-700 hover:border-blue-500/30'
                      }`}
                    >
                      <Checkbox
                        checked={selectedChildrenToLink.includes(player.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedChildrenToLink([...selectedChildrenToLink, player.id]);
                          } else {
                            setSelectedChildrenToLink(selectedChildrenToLink.filter(id => id !== player.id));
                          }
                        }}
                      />
                      <div className="flex-1">
                        <span className="font-medium text-white">{player.name || player.child_name}</span>
                        {player.membership_number && (
                          <span className="ml-2 text-sm text-slate-400">#{player.membership_number}</span>
                        )}
                        <span className="ml-2 text-xs text-slate-500">{player.age_group || player.team_id}</span>
                        {player.parent_user_id && (
                          <span className="ml-2 text-xs text-amber-400">(already linked to another parent)</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              {selectedChildrenToLink.length > 0 && (
                <Button
                  onClick={handleLinkChildren}
                  className="mt-4 w-full bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Link {selectedChildrenToLink.length} Selected Child(ren)
                </Button>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setCreateChildOpen(true); setNewChild({ given_name: "", last_name: linkingParent?.name?.split(" ").pop() || "", birthdate: "", gender: "male", medical_conditions: "" }); }}
              className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" data-testid="create-child-btn">
              <Plus className="w-4 h-4 mr-2" />Create New Child
            </Button>
            <Button variant="outline" onClick={() => setLinkChildrenDialogOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Child Dialog */}
      <Dialog open={createChildOpen} onOpenChange={setCreateChildOpen}>
        <DialogContent className="max-w-md" data-testid="create-child-dialog">
          <DialogHeader>
            <DialogTitle>Create Child for {linkingParent?.name}</DialogTitle>
            <DialogDescription>Add a new child record and link to this parent. A membership number will be generated automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Given Name *</label>
                <Input value={newChild.given_name} onChange={(e) => setNewChild(p => ({ ...p, given_name: e.target.value }))}
                  placeholder="Given name" data-testid="child-given-name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Name *</label>
                <Input value={newChild.last_name} onChange={(e) => setNewChild(p => ({ ...p, last_name: e.target.value }))}
                  placeholder="Last name" data-testid="child-last-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                <Input type="date" value={newChild.birthdate} onChange={(e) => setNewChild(p => ({ ...p, birthdate: e.target.value }))}
                  data-testid="child-birthdate" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Gender</label>
                <select value={newChild.gender} onChange={(e) => setNewChild(p => ({ ...p, gender: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 bg-slate-800 border-slate-600 text-white" data-testid="child-gender">
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Medical Conditions / Allergies</label>
              <textarea value={newChild.medical_conditions} onChange={(e) => setNewChild(p => ({ ...p, medical_conditions: e.target.value }))}
                placeholder="Any conditions we should know about..."
                className="w-full border rounded-md px-3 py-2 min-h-[60px] bg-slate-800 border-slate-600 text-white placeholder:text-slate-400"
                data-testid="child-medical" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateChildOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleCreateChild}
              disabled={creatingChild || !newChild.given_name || !newChild.last_name || !newChild.birthdate}
              data-testid="confirm-create-child-btn">
              {creatingChild ? "Creating..." : "Create Child"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
